import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  HelpCircle, ChevronRight, ChevronDown, ArrowLeft,
  Calendar, Car, CreditCard, Shield, Bell, User, Clock,
  MapPin, AlertCircle, CheckCircle2
} from 'lucide-react';

// FAQ Data estruturado
const FAQ_CATEGORIES = [
  {
    id: 'agendamento',
    title: 'Agendamento',
    icon: Calendar,
    items: [
      {
        question: 'Como agendar uma lavagem?',
        answer: 'Na tela inicial, clique em "Agendar Lavagem". Selecione a data, horário e escolha o lava-jato mais próximo. Confirme o agendamento e aguarde a confirmação do prestador.'
      },
      {
        question: 'Posso cancelar um agendamento?',
        answer: 'Sim, você pode cancelar agendamentos pendentes ou confirmados diretamente na aba "Agendamentos". Cancelamentos após o início do serviço não são permitidos.'
      },
      {
        question: 'Como sei se meu agendamento foi confirmado?',
        answer: 'Você receberá uma notificação quando o lava-jato confirmar seu agendamento. O status mudará de "Aguardando" para "Confirmado" na sua lista de agendamentos.'
      },
      {
        question: 'O que acontece se o lava-jato não confirmar?',
        answer: 'Se o prestador não confirmar em até 10 minutos, você pode cancelar sem penalidades e agendar em outro lava-jato.'
      }
    ]
  },
  {
    id: 'pagamento',
    title: 'Pagamento',
    icon: CreditCard,
    items: [
      {
        question: 'Quando sou cobrado?',
        answer: 'O pagamento é processado apenas após a conclusão do serviço e confirmação do código. Você não paga antecipadamente.'
      },
      {
        question: 'O que é o código de confirmação?',
        answer: 'É um código de 6 dígitos que você recebe após o serviço. Informe este código ao prestador para liberar o pagamento. Nunca compartilhe o código antes do serviço ser concluído.'
      },
      {
        question: 'Como funciona o plano mensal?',
        answer: 'Com um plano, você paga um valor fixo mensal e tem direito a um número determinado de lavagens. É mais econômico para quem lava o carro regularmente.'
      }
    ]
  },
  {
    id: 'servico',
    title: 'Serviço',
    icon: Car,
    items: [
      {
        question: 'Quais tipos de lavagem estão disponíveis?',
        answer: 'Oferecemos Lavagem Completa (interna e externa) e Ducha Simples (apenas externa). Os serviços e preços variam conforme o tipo de veículo.'
      },
      {
        question: 'Quanto tempo dura uma lavagem?',
        answer: 'Em média, uma Lavagem Completa leva de 40 a 60 minutos. A Ducha Simples leva cerca de 15 a 20 minutos.'
      },
      {
        question: 'Posso acompanhar o status do serviço?',
        answer: 'Sim! Na aba "Agendamentos" você vê o status em tempo real: Aguardando, Confirmado, Em execução e Finalizado.'
      }
    ]
  },
  {
    id: 'conta',
    title: 'Minha Conta',
    icon: User,
    items: [
      {
        question: 'Como altero meus dados?',
        answer: 'Acesse seu perfil através do menu e edite suas informações pessoais, veículo cadastrado ou forma de pagamento.'
      },
      {
        question: 'Esqueci minha senha',
        answer: 'Na tela de login, clique em "Esqueceu a senha?" e siga as instruções para redefinir através do seu e-mail.'
      },
      {
        question: 'Como verifico meu e-mail?',
        answer: 'Enviamos um link de verificação para seu e-mail ao cadastrar. Clique no link ou solicite o reenvio na tela de login.'
      }
    ]
  },
  {
    id: 'seguranca',
    title: 'Segurança',
    icon: Shield,
    items: [
      {
        question: 'Meus dados estão seguros?',
        answer: 'Sim! Utilizamos criptografia e seguimos as melhores práticas de segurança para proteger seus dados pessoais e de pagamento.'
      },
      {
        question: 'Como denuncio um problema com o serviço?',
        answer: 'Se tiver problemas com um serviço, não confirme o código até que seja resolvido. Relate o ocorrido e nossa equipe analisará o caso.'
      }
    ]
  }
];

export default function HelpCenter() {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    setExpandedQuestion(null);
  };

  const toggleQuestion = (questionIndex) => {
    setExpandedQuestion(expandedQuestion === questionIndex ? null : questionIndex);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-full">
              <HelpCircle className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Central de Ajuda</h1>
              <p className="text-xs text-muted-foreground">Dúvidas frequentes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Busca rápida info */}
        <Card className="p-4 bg-primary/5 border-primary/20">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium text-sm">Encontre respostas rápidas</p>
              <p className="text-xs text-muted-foreground">
                Selecione uma categoria abaixo para ver as perguntas frequentes
              </p>
            </div>
          </div>
        </Card>

        {/* Categorias FAQ */}
        <div className="space-y-3">
          {FAQ_CATEGORIES.map((category) => {
            const IconComponent = category.icon;
            const isExpanded = expandedCategory === category.id;
            
            return (
              <Card key={category.id} className="overflow-hidden">
                {/* Cabeçalho da categoria */}
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                  data-testid={`faq-category-${category.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-primary text-white' : 'bg-slate-100'}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{category.title}</span>
                    <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                      {category.items.length} perguntas
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>

                {/* Perguntas da categoria */}
                {isExpanded && (
                  <div className="border-t">
                    {category.items.map((item, index) => {
                      const questionId = `${category.id}-${index}`;
                      const isQuestionExpanded = expandedQuestion === questionId;
                      
                      return (
                        <div key={index} className="border-b last:border-b-0">
                          <button
                            className="w-full p-4 pl-6 text-left hover:bg-slate-50 transition-colors flex items-start justify-between gap-4"
                            onClick={() => toggleQuestion(questionId)}
                            data-testid={`faq-question-${questionId}`}
                          >
                            <span className={`text-sm ${isQuestionExpanded ? 'font-medium text-primary' : ''}`}>
                              {item.question}
                            </span>
                            {isQuestionExpanded ? (
                              <ChevronDown className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            )}
                          </button>
                          
                          {isQuestionExpanded && (
                            <div className="px-6 pb-4">
                              <p className="text-sm text-muted-foreground bg-slate-50 p-3 rounded-lg">
                                {item.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Info adicional */}
        <Card className="p-4 bg-slate-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium text-sm">Não encontrou o que procura?</p>
              <p className="text-xs text-muted-foreground mt-1">
                Estamos trabalhando para melhorar nossa central de ajuda. 
                Em breve teremos mais opções de suporte.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
